import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Lock } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import FAQ from "../components/FAQ";
import CaseStudyGate, { isCaseStudyUnlocked } from "../components/CaseStudyGate";
import { projects } from "../lib/data";

// Scroll progress bar at top
const ProgressBar = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);
  return (
    <motion.div
      style={{ scaleX, transformOrigin: "left" }}
      className="fixed top-0 left-0 right-0 h-[3px] bg-[color:var(--nn-accent)] z-[60]"
      aria-hidden
    />
  );
};

// Sticky chapter navigator on the right — highlights the active section
const ChapterNav = ({ chapters }) => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.getAttribute("data-chapter-idx"));
            if (!Number.isNaN(idx)) setActive(idx);
          }
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );
    document.querySelectorAll("[data-chapter-idx]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [chapters]);

  const jump = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav className="hidden lg:flex chapter-rail" aria-label="Case study chapters">
      {chapters.map((c, i) => (
        <button
          key={c.id}
          onClick={() => jump(c.id)}
          data-cursor="hover"
          data-testid={`chapter-nav-${i}`}
          className={`group flex items-center justify-end gap-3 pr-2 transition-colors ${
            active === i ? "text-[color:var(--nn-text)]" : "text-[color:var(--nn-muted)]"
          }`}
        >
          <span
            className={`transition-opacity ${
              active === i ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            }`}
          >
            {c.label}
          </span>
          <span
            className={`block w-6 h-[2px] transition-all ${
              active === i ? "bg-[color:var(--nn-accent)] w-10" : "bg-[color:var(--nn-border)]"
            }`}
          />
        </button>
      ))}
    </nav>
  );
};

// Connective bridge between sections — a thin animated line + label
export const Bridge = ({ label = "next" }) => (
  <div className="relative h-16 md:h-24 flex items-center justify-center">
    <motion.div
      initial={{ scaleY: 0 }}
      whileInView={{ scaleY: 1 }}
      viewport={{ once: true, margin: "-30% 0px -30% 0px" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="w-[1px] h-full bg-[color:var(--nn-accent)]"
      style={{ transformOrigin: "top" }}
    />
    <span className="absolute top-1/2 -translate-y-1/2 bg-[color:var(--nn-bg)] px-3 font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)]">
      ↓ {label}
    </span>
  </div>
);

const CaseStudyLayout = ({ project, chapters = [], children }) => {
  const other = projects.find((p) => p.slug !== project.slug);
  const hero = useRef(null);
  const { scrollYProgress } = useScroll({ target: hero, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const [unlocked, setUnlocked] = useState(() =>
    project.nda ? isCaseStudyUnlocked(project.slug) : true
  );
  const isLocked = project.nda && !unlocked;

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      data-testid={`case-study-${project.slug}`}
    >
      <Navigation />
      <ProgressBar />
      {!isLocked && <ChapterNav chapters={chapters} />}

      {/* Hero */}
      <section ref={hero} className="pt-32 md:pt-40 pb-10 md:pb-16 relative">
        <motion.div style={{ y: heroY }} className="mx-auto max-w-[1600px] px-5 md:px-10">
          <Link
            to="/#work"
            data-cursor="hover"
            data-testid="back-home"
            className="inline-flex items-center gap-2 text-sm text-[color:var(--nn-muted)] hover:text-[color:var(--nn-accent-text)] transition-colors mb-8 group"
          >
            <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" /> All work
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="chip chip-accent">({project.index})</span>
            <span className="chip">{project.year}</span>
            {project.tags.map((t) => (
              <span key={t} className="chip">{t}</span>
            ))}
          </div>
          <h1 className="h-display text-[14vw] md:text-[9vw] lg:text-[7.5vw] leading-[0.85] tracking-tighter mb-8">
            {project.title}
          </h1>
          <p className="max-w-2xl text-lg md:text-2xl text-[color:var(--nn-muted)] leading-snug font-serif-i">
            {project.summary}
          </p>

          {project.nda && (
            <div
              className="mt-8 inline-flex items-start gap-3 max-w-xl p-4 rounded-2xl border border-[color:var(--nn-accent)]/40 bg-[color:var(--nn-accent)]/5"
              data-testid="nda-note"
            >
              <div className="w-8 h-8 shrink-0 rounded-full grid place-items-center bg-[color:var(--nn-accent)] text-[color:var(--nn-accent-ink)]">
                <Lock size={15} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-accent-text)] mb-1">
                  NDA Project
                </div>
                <p className="text-sm text-[color:var(--nn-text)]/80 leading-relaxed">
                  Visuals here are limited and lightly abstracted to respect an active NDA. Happy to
                  walk through the full flow, decisions, and prototypes live — just{" "}
                  <a
                    href="/#contact"
                    data-cursor="hover"
                    className="text-[color:var(--nn-text)] link-underline"
                  >
                    reach out
                  </a>
                  .
                </p>
              </div>
            </div>
          )}

          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-[color:var(--nn-border)] pt-10">
            {[
              { l: "Role", v: project.role },
              { l: "Timeline", v: project.timeline },
              { l: "Year", v: project.year },
              { l: "Tools", v: project.tools.join(" · ") },
            ].map((m) => (
              <div key={m.l}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mb-2">
                  {m.l}
                </div>
                <div className="text-base md:text-lg">{m.v}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Cover — only render when we have a real asset AND not locked */}
      {project.assets?.cover && !isLocked && (
        <section className="pb-16 md:pb-20">
          <div className="mx-auto max-w-[1600px] px-5 md:px-10">
            <motion.figure
              initial={{ y: 60, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-3xl overflow-hidden border border-[color:var(--nn-border)] group"
              data-testid={`cover-${project.slug}`}
            >
              <img
                src={project.assets.cover}
                alt={`${project.title} — hero`}
                className="w-full h-auto object-cover object-top transition-transform duration-[1400ms] group-hover:scale-[1.01]"
              />
            </motion.figure>
          </div>
        </section>
      )}

      {isLocked ? (
        <CaseStudyGate project={project} onUnlock={() => setUnlocked(true)} />
      ) : (
        <>
          {children}

          {/* Outcomes */}
          <section className="py-24 md:py-36 border-t border-[color:var(--nn-border)]" id="outcomes" data-chapter-idx={chapters.length}>
            <div className="mx-auto max-w-[1600px] px-5 md:px-10">
              <div className="section-label mb-6">Outcomes · Reflection</div>
              <h2 className="h-display text-5xl md:text-7xl lg:text-8xl mb-14">
                Impact at a <span className="font-serif-i text-[color:var(--nn-muted)]">glance.</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {project.metrics.map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: i * 0.1 }}
                    className="group relative p-8 rounded-3xl border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/50 backdrop-blur overflow-hidden"
                  >
                    <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[color:var(--nn-accent)]/10 blur-3xl group-hover:bg-[color:var(--nn-accent)]/25 transition-colors" />
                    <div className="relative">
                      <div className="font-mono text-[10px] text-[color:var(--nn-muted)] mb-3">
                        METRIC · 0{i + 1}
                      </div>
                      <div className="h-display text-6xl md:text-8xl text-[color:var(--nn-accent-text)] mb-3">
                        {m.value}
                      </div>
                      <div className="text-sm text-[color:var(--nn-muted)]">{m.label}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Next project */}
          {other && (
            <section className="border-t border-[color:var(--nn-border)]">
              <Link
                to={`/work/${other.slug}`}
                data-cursor="dragging"
                data-cursor-label="next project"
                data-testid="next-project"
                className="group block relative aspect-[21/9] overflow-hidden bg-[color:var(--nn-surface)]"
              >
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-40"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, var(--nn-border) 1px, transparent 1px), linear-gradient(to bottom, var(--nn-border) 1px, transparent 1px)",
                    backgroundSize: "44px 44px",
                  }}
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mb-4">
                    Next Project — ({other.index})
                  </div>
                  <h3 className="h-display text-5xl md:text-8xl tracking-tighter flex items-center gap-4 group-hover:text-[color:var(--nn-accent-text)] transition-colors">
                    {other.title}
                    <ArrowUpRight className="transition-transform group-hover:rotate-45 duration-500 text-[color:var(--nn-accent)]" size={36} />
                  </h3>
                </div>
              </Link>
            </section>
          )}
        </>
      )}

      <FAQ />
      <Footer />
    </motion.main>
  );
};

export default CaseStudyLayout;
